import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import moment from "moment";
import "moment/locale/ru";
import ArrowDownIcon from "../../shared/icons/ArrowDownIcon";
import TenderAssignmentMethodIcon from "../../shared/icons/TenderAssignmentMethodIcon";
import AuctionAssignmentMethodIcon from "../../shared/icons/AuctionAssignmentMethodIcon";
import { numberToFinanceFormat } from "../../../utils";
import CustomButton from "../../custom/custom-button/CustomButton";
import OrdersSectionItemTableStatus from "../../orders-new/section-item/components/OrdersSectionItemTableStatus";
import OrderInfoAviation from "../order-info/OrderInfoAviation";
import OrderInfoAviationExpeditor from "../order-info-expeditor/OrderInfoAviationExpeditor";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CURRENCIES } from "../../../mock";
import NotificationIconNew from "../../shared/icons/NotificationIconNew";
import NotificationsPopup from "../../notification/NotificationsPopup/NotificationsPopup";
import { getCountryIcon } from "../../../utils/getCountryIcon";
import OrderInfoAviationMarketplace from "../order-info/components/OrderInfoAviation/Marketplace";
import AppTooltip from "../../shared/AppTooltip/AppTooltip";
import { isUnloadingActionRequired } from "../../orders-new/available/tender-orders/utils/isUnloadingActionRequired";

moment.locale("ru");

const OrdersAviationTable = ({
  items = [],
  showTable = true,
  isExpeditor,
  companyName,
  onForceUpdate,
  isShowOrderSolution,
  notificationsMap = {},
  setNotificationsMap,
  isMarketplacePage = false,
  selectedIds = [],
  setSelectedIds = () => {},
  unselectedIds = [],
  setUnselectedIds = () => {},
  selectionMode = "partial",
}) => {
  const { t } = useTranslation();
  const tableRef = useRef(null);
  const notificationRefs = useRef([]);

  const [isOpenInfo, setIsOpenInfo] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const { user } = useSelector((state) => state);
  const userCompanyId = user?.companyId ?? user?.company?.id;
  const [openNotificationOrderId, setOpenNotificationOrderId] = useState(null);

  const handleToggleNotification = (orderId) => {
    setOpenNotificationOrderId((prevId) => (prevId === orderId ? null : orderId));
  };
  
  const currentPageIds = useMemo(() => items.map((item) => item.id).filter(Boolean), [items]);
  const isOrderSelected = useCallback(
    (itemId) => {
      if (selectionMode === "all") {
        return !unselectedIds.includes(itemId);
      }

      return selectedIds.includes(itemId);
    },
    [selectedIds, selectionMode, unselectedIds],
  );
  const isCurrentPageSelected = currentPageIds.length > 0 && currentPageIds.every(isOrderSelected);

  const handleToggleCheckbox = (itemId) => {
    if (selectionMode === "all") {
      setUnselectedIds((prev) => {
        if (prev.includes(itemId)) {
          return prev.filter((id) => id !== itemId);
        }

        return [...prev, itemId];
      });
      return;
    }

    setSelectedIds((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      }

      return [...prev, itemId];
    });
  };

  const handleToggleSelectAll = () => {
    if (selectionMode === "all") {
      setUnselectedIds((prev) => {
        if (isCurrentPageSelected) {
          return Array.from(new Set([...prev, ...currentPageIds]));
        }

        return prev.filter((id) => !currentPageIds.includes(id));
      });
      return;
    }

    setSelectedIds((prev) => {
      if (isCurrentPageSelected) {
        return prev.filter((id) => !currentPageIds.includes(id));
      }

      return Array.from(new Set([...prev, ...currentPageIds]));
    });
  };

  const handleCheckboxChange = (event) => {
    event.stopPropagation();
  };

  const handleOpenOrderInfo = (orderInfo, orderId) => {
    setSelectedOrderId(null);
    setSelectedOrderId(orderId);
    setOrderInfo(orderInfo);
    setIsOpenInfo(true);
  };

  const handleClickOutside = (event) => {
    if (event.target.closest?.('[data-portal-modal="true"]')) return;

    if (tableRef.current && !tableRef.current.contains(event.target)) {
      setIsOpenInfo(false);
    }
  };

  useEffect(() => {
    if (isOpenInfo) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpenInfo]);

  const tableHead = useMemo(() => {
    const columns = [
      {
        name: "",
        isSortable: false,
        key: "checkbox",
      },
      {
        name: t("ordersAvailablePage.tableHead.details"),
        isSortable: false,
        key: "number",
      },
      {
        name: t("ordersAvailablePage.tableHead.from"),
        isSortable: false,
        key: "addressFrom",
      },
      {
        name: t("ordersAvailablePage.tableHead.to"),
        isSortable: false,
        key: "addressTo",
      },
      {
        name: t("ordersAvailablePage.tableHead.status"),
        isSortable: false,
      },
      {
        name: "",
        isSortable: false,
      },
      {
        name: "",
        isSortable: false,
      },
    ];

    return columns;
  }, [user?.type, t]);

  return (
    <div className={cn(style.table_wrapper, "table-responsive")} ref={tableRef}>
      {showTable && (
        <table className="table">
        <thead>
          <tr>
            {tableHead.map((item, idx) => (
              <th scope="col" key={"head-" + idx}>
                {idx === 0 ? (
                  <div className={style.checkboxWrapper} onClick={handleToggleSelectAll}>
                    <input
                      type="checkbox"
                      className={style.checkboxTable}
                      checked={isCurrentPageSelected}
                      onChange={handleCheckboxChange}
                      readOnly
                    />
                    <div className={style.checkboxCustom}>
                      {isCurrentPageSelected && (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={style.tableHead}>{item.component ? item.component : item.name}</div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            if (!notificationRefs.current[idx]) {
              notificationRefs.current[idx] = React.createRef();
            }
            const unreadCount = (notificationsMap[item.id] || []).filter((n) => !n.isRead).length;
            const isOtherExpeditorAssigned = Boolean(
              isExpeditor &&
              item?.expeditorId != null &&
              item.expeditorId !== "" &&
              userCompanyId != null &&
              userCompanyId !== "" &&
              String(item.expeditorId) !== String(userCompanyId),
            );
            const isNotificationDisabled =
              isOtherExpeditorAssigned && item?.status?.toLowerCase() !== "awaitingoffer";
            const requiresAction = Boolean(isExpeditor && isUnloadingActionRequired(item));
            const AssignmentIcon =
              item.status?.toLowerCase() === "awaitingoffer" || isNotificationDisabled
                ? { Tender: TenderAssignmentMethodIcon, Auction: AuctionAssignmentMethodIcon }[item.assignmentMethod]
                : null;

            return (
              <tr key={idx}>
                <td>
                  <div
                    className={style.checkboxWrapper}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleCheckbox(item.id);
                    }}
                  >
                    <input
                      type="checkbox"
                      className={style.checkboxTable}
                      checked={isOrderSelected(item.id)}
                      onChange={handleCheckboxChange}
                      readOnly
                    />
                    <div className={style.checkboxCustom}>
                      {isOrderSelected(item.id) && (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                </td>
                <td onClick={() => handleOpenOrderInfo(item, item.id)} className="cursor-pointer">
                  <div className={style.table_col} style={item?.referenceNumber ? { gap: 0 } : undefined}>
                    {AssignmentIcon ? (
                      <div className={style.table_col_vehicles}>
                        <AssignmentIcon
                          color="#004de6"
                          role="img"
                          aria-label={t(item.assignmentMethod === "Auction"
                            ? "ordersAvailablePage.auctionInProgress"
                            : "ordersAvailablePage.tenderInProgress")}
                        />
                      </div>
                    ) : !isOtherExpeditorAssigned ? (
                      <span>
                        {numberToFinanceFormat(item.price)} {CURRENCIES[item?.currency || "EUR"]?.symbol || ""}
                      </span>
                    ) : null}
                    <span className={style.table_col_summary}>№ {item.number}</span>
                    {item?.referenceNumber && (
                      <>
                        <span
                          className={cn(style.table_col_summary, style.table_col_reference)}
                          data-tooltip-id={`referenceNumber-tooltip-${item.id}`}
                        >
                          №{item?.referenceNumber}
                        </span>
                        <AppTooltip
                          id={`referenceNumber-tooltip-${item.id}`}
                          positionStrategy="fixed"
                          delayShow={50}
                        >
                          №{item?.referenceNumber}
                        </AppTooltip>
                      </>
                    )}
                  </div>
                </td>
                <td onClick={() => handleOpenOrderInfo(item, item.id)} className="cursor-pointer">
                  <div className={style.table_col}>
                    <div className={style.table_col_country}>
                      <img src={getCountryIcon(item.countryIdFrom)} className={style.table_col_countryIcon} />
                      {item.cityFromName}
                    </div>
                    <span
                      className={cn(style.table_col_summary, style.table_col_address)}
                      data-tooltip-id={`aviation-from-tooltip-${item.id}`}
                    >
                      {item?.senderName} / {item?.addressFrom}
                    </span>
                    <AppTooltip id={`aviation-from-tooltip-${item.id}`} positionStrategy="fixed" delayShow={50}>
                      {item?.senderName} / {item?.addressFrom}
                    </AppTooltip>
                  </div>
                </td>
                <td onClick={() => handleOpenOrderInfo(item, item.id)} className="cursor-pointer">
                  <div className={style.table_col}>
                    <div className={style.table_col_country}>
                      <img src={getCountryIcon(item.countryIdTo)} className={style.table_col_countryIcon} />
                      {item.cityToName}
                    </div>
                    <span
                      className={cn(style.table_col_summary, style.table_col_address)}
                      data-tooltip-id={`aviation-to-tooltip-${item.id}`}
                    >
                      {item?.addressTo}
                    </span>
                    <AppTooltip id={`aviation-to-tooltip-${item.id}`} positionStrategy="fixed" delayShow={50}>
                      {item?.addressTo}
                    </AppTooltip>
                  </div>
                </td>
                <td onClick={() => handleOpenOrderInfo(item, item.id)} className="cursor-pointer">
                  <div>
                    <OrdersSectionItemTableStatus
                      timeSnapshotKey={item}
                      status={item.status}
                      driverName={item.driverName}
                      driverSurname={item.driverSurname}
                      expeditorName={item?.expeditorName}
                      expeditorId={item?.expeditorId}
                      isExpeditor={isExpeditor}
                      tenderDateEnd={item?.tenderDateEnd}
                      assignmentMethod={item?.assignmentMethod}
                      requiresAction={requiresAction}
                    />
                  </div>
                </td>
                <td onClick={() => handleOpenOrderInfo(item, item.id)} className="cursor-pointer">
                  <div className={style.table_col}>
                    <div className={style.table_col_country}>{item.vehicleTypeName}</div>
                    <span className={style.table_col_summary}>{item.carModel}</span>
                  </div>
                  {isShowOrderSolution && item.status === "awaitingoffer" && (
                    <CustomButton text={t("orderInfo.suggestPrice.suggestButton")} isMini />
                  )}
                  {isShowOrderSolution && item.status === "confirmed" && (
                    <CustomButton text={t("decisionTitle")} isMini />
                  )}
                  {isShowOrderSolution && item.status?.toLowerCase() === "expeditorrejected" && (
                    <CustomButton text={t("mockData.orderStatuses.rejected")} isMini isWhite />
                  )}
                </td>
                <td>
                  <div className={style.table_col_arrow}>
                    <div
                      className={cn("ms-auto", style.notification, {
                        [style.notification_disabled]: isNotificationDisabled,
                      })}
                      aria-disabled={isNotificationDisabled}
                      ref={notificationRefs.current[idx]}
                      data-tooltip-id={`aviation-notif-tooltip-${item.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isNotificationDisabled) return;
                        handleToggleNotification(item.id);
                      }}
                    >
                      <NotificationIconNew />
                      {!isNotificationDisabled && unreadCount > 0 && (
                        <div className={style.notification_count}>{unreadCount}</div>
                      )}
                    </div>
                    <AppTooltip id={`aviation-notif-tooltip-${item.id}`} place="left" positionStrategy="fixed">
                      {t('tooltips.orderNotifications')}
                    </AppTooltip>

                    <NotificationsPopup
                      notifications={notificationsMap[item.id] || []}
                      orderData={item}
                      orderType="aviation"
                      isOpen={!isNotificationDisabled && openNotificationOrderId === item.id}
                      onClose={() => {
                        setOpenNotificationOrderId(null);
                      }}
                      referenceElement={notificationRefs.current[idx]?.current}
                      onNotificationsRead={(readIds) => {
                        setNotificationsMap((prev) => ({
                          ...prev,
                          [item.id]: prev[item.id].map((notif) =>
                            readIds.includes(notif.id)
                              ? { ...notif, isRead: true, readDate: new Date() }
                              : notif,
                          ),
                        }));
                      }}
                    />

                    <ArrowDownIcon
                      style={{ transform: "rotate(270deg)" }}
                      onClick={() => handleOpenOrderInfo(item, item.id)}
                      className="cursor-pointer"
                      data-tooltip-id={`aviation-arrow-tooltip-${item.id}`}
                    />
                    <AppTooltip id={`aviation-arrow-tooltip-${item.id}`} place="left" positionStrategy="fixed">
                      {t('tooltips.viewOrderDetails')}
                    </AppTooltip>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        </table>
      )}
      {isMarketplacePage ? (
        <OrderInfoAviationMarketplace
          isOpen={isOpenInfo}
          setIsOpen={setIsOpenInfo}
          orderId={selectedOrderId}
          companyName={companyName}
          onForceUpdate={onForceUpdate}
          tenderOrderInfo={orderInfo}
        />
      ) : isExpeditor ? (
        <OrderInfoAviationExpeditor
          isOpen={isOpenInfo}
          setIsOpen={setIsOpenInfo}
          orderId={selectedOrderId}
          companyName={companyName}
          onForceUpdate={onForceUpdate}
          tenderOrderInfo={orderInfo}
        />
      ) : (
        <OrderInfoAviation
          isOpen={isOpenInfo}
          setIsOpen={setIsOpenInfo}
          orderId={selectedOrderId}
          companyName={companyName}
          onForceUpdate={onForceUpdate}
          tenderOrderInfo={orderInfo}
          isShowOrderSolution={isShowOrderSolution}
        />
      )}
    </div>
  );
};

export default OrdersAviationTable;
